class CollectionFilters {
  constructor() {
    this.filterForm = document.querySelector('[data-filter-form]');
    this.productGrid = document.querySelector('[data-products-grid]');
    this.filterContainer = document.querySelector('[data-filters-container]');
    this.loadingIndicator = document.querySelector('[data-loading-indicator]');
    this.mobileToggle = document.querySelector('[data-mobile-filter-toggle]');
    this.filterSidebar = document.querySelector('[data-filter-sidebar]');
    this.sortBySelect = document.querySelector('[data-sort-by]');

    if (this.filterForm) this.init();
  }

  /* -------------------------
     Helpers
  ------------------------- */

  normalizeTagHandle(raw) {
    if (!raw && raw !== 0) return '';
    return String(raw)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-') // non-alphanum → hyphen
      .replace(/-+/g, '-') // collapse
      .replace(/^-|-$/g, '');
  }

  getCollectionBasePath(pathname = window.location.pathname) {
    const parts = pathname.split('/').filter(Boolean);
    const idx = parts.indexOf('collections');
    if (idx >= 0 && parts.length > idx + 1) {
      return '/' + parts.slice(0, idx + 2).join('/');
    }
    return '/' + (parts.slice(0, 2).join('/'));
  }

  getPathTagSegment(url = window.location.href) {
    const urlObj = new URL(url, window.location.origin);
    const parts = urlObj.pathname.split('/').filter(Boolean);
    const idx = parts.indexOf('collections');
    if (idx >= 0 && parts.length > idx + 2) {
      return parts.slice(idx + 2).join('/');
    }
    return null;
  }

  getPathTagsArray(url = window.location.href) {
    const seg = this.getPathTagSegment(url);
    if (!seg) return [];
    return seg.split('+').filter(Boolean).map(t => {
      // If tag doesn't already have "car-parts-" prefix, add "Car Parts:" before normalizing
      const tagWithPrefix = t.startsWith('car-parts-') ? t : 'Car Parts:' + t;
      return this.normalizeTagHandle(tagWithPrefix);
    });
  }

  getQueryFilters(url = window.location.href) {
    const urlObj = new URL(url, window.location.origin);
    const map = {};
    for (const [k, v] of urlObj.searchParams) {
      if (k.startsWith('filter.')) {
        if (!map[k]) map[k] = [];
        map[k].push(v);
      } else {
        map._other = map._other || {};
        map._other[k] = v;
      }
    }
    return map;
  }

  buildUrl(basePath, tagsArray = [], params = new URLSearchParams()) {
    let path = basePath;
    if (tagsArray.length) path += '/' + tagsArray.join('+');
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  }

  /* -------------------------
     Tag (path-based) toggle
  ------------------------- */
  togglePathTag(labelText) {
    const cleaned = String(labelText).replace(/\s*\(\d+\)$/, '').trim();
    const candidateHandle = this.normalizeTagHandle('Car Parts:' + cleaned);

    const base = this.getCollectionBasePath();
    const currentTags = this.getPathTagsArray(window.location.href);

    const exists = currentTags.includes(candidateHandle);
    const newTags = exists
      ? currentTags.filter(t => t !== candidateHandle)
      : [...currentTags, candidateHandle];

    const qs = new URLSearchParams(window.location.search);
    qs.delete('page');

    const finalUrl = this.buildUrl(base, newTags, qs);
    history.pushState({ filtersUrl: finalUrl }, '', finalUrl);
    this.loadFilters(finalUrl, false);
  }

  /* -------------------------
     Search & Discovery (query) toggle
  ------------------------- */
  toggleQueryFilter(key, rawValue) {
    const value = String(rawValue).replace(/\s*\(\d+\)$/, '').trim();

    const urlObj = new URL(window.location.href);
    const params = urlObj.searchParams;
    const existing = params.getAll(key);

    const exists = existing.some(v => v.toLowerCase() === value.toLowerCase());

    params.delete(key);
    if (!exists) {
      existing.forEach(v => {
        if (v.toLowerCase() !== value.toLowerCase()) params.append(key, v);
      });
      params.append(key, value);
    } else {
      existing.forEach(v => {
        if (v.toLowerCase() !== value.toLowerCase()) params.append(key, v);
      });
    }

    params.delete('page');

    const base = this.getCollectionBasePath();
    const pathTags = this.getPathTagsArray().join('+');
    const path = pathTags ? `${base}/${pathTags}` : base;

    const finalUrl = this.buildUrl(path, [], params);
    history.pushState({ filtersUrl: finalUrl }, '', finalUrl);
    this.loadFilters(finalUrl, false);
  }

  /* -------------------------
     Decide filter type (path vs query)
  ------------------------- */
  handleFilterClickFromElement(linkEl) {
    const key = linkEl.getAttribute('data-filter-key');
    const val = linkEl.getAttribute('data-filter-value');

    if (key && val) {
      this.toggleQueryFilter(key, val);
      return;
    }

    const labelText =
      linkEl.querySelector('label')?.textContent || linkEl.textContent;
    this.togglePathTag(labelText);
  }

  /* -------------------------
     Init + Events
  ------------------------- */
  init() {
    this.initializeSortFromURL();

    document.addEventListener(
      'click',
      e => {
        const filterLink = e.target.closest('[data-filter-link]');
        if (filterLink) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.handleFilterClickFromElement(filterLink);
          return false;
        }

        const mobileToggle = e.target.closest('[data-mobile-filter-toggle]');
        if (mobileToggle) {
          e.preventDefault();
          this.toggleMobileFilters();
        }

        // Handle pagination clicks
        const paginationLink = e.target.closest('.pagination a');
        if (paginationLink) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.handlePaginationClick(paginationLink);
          return false;
        }
      },
      true
    );

    document.addEventListener('submit', e => {
      const priceForm = e.target.closest('[data-price-form]');
      if (priceForm) {
        e.preventDefault();
        e.stopPropagation();
        this.handlePriceSubmit(priceForm);
        return false;
      }
    });

    document.addEventListener('change', e => {
      const sortBySelect = e.target.closest('[data-sort-by]');
      if (sortBySelect) this.handleSortChange(sortBySelect);
    });

    window.addEventListener('popstate', e => {
      const url =
        e.state && e.state.filtersUrl ? e.state.filtersUrl : window.location.href;
      this.loadFilters(url, false);
    });
  }

  /* -------------------------
     AJAX loader
  ------------------------- */
  async loadFilters(url, updateHistory = false) {
    try {
      this.showLoading();
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response not ok');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newProductGrid = doc.querySelector('[data-products-grid]');
      if (newProductGrid && this.productGrid) {
        this.productGrid.innerHTML = newProductGrid.innerHTML;
        const gridTop = this.productGrid.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: gridTop, behavior: 'smooth' });
      }

      const newFilters = doc.querySelector('[data-filters-container]');
      if (newFilters && this.filterContainer) {
        this.updateFilterStates(newFilters);
        this.restoreSortSelection(url);
      }

      const newPagination = doc.querySelector('.pagination');
      const currentPagination = document.querySelector('.pagination');
      if (newPagination && currentPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
      } else if (!newPagination && currentPagination) {
        currentPagination.style.display = 'none';
      } else if (newPagination && !currentPagination) {
        const paginationContainer = document.createElement('div');
        paginationContainer.className = 'pagination';
        paginationContainer.innerHTML = newPagination.innerHTML;
        this.productGrid.parentNode.appendChild(paginationContainer);
      }

      if (updateHistory) {
        const newUrl = new URL(url, window.location.origin);
        history.pushState({ filtersUrl: url }, '', newUrl);
      }

      document.dispatchEvent(
        new CustomEvent('collection:filtered', { detail: { url } })
      );
    } catch (err) {
      console.error('Error loading filters:', err);
    } finally {
      this.hideLoading();
    }
  }

  /* -------------------------
     Filter state update
  ------------------------- */
  updateFilterStates(newFilters) {
    const currentFilterLinks = this.filterContainer.querySelectorAll(
      '[data-filter-link]'
    );
    const newFilterLinks = newFilters.querySelectorAll('[data-filter-link]');

    const newFilterData = new Map();
    newFilterLinks.forEach(link => {
      const href = link.getAttribute('href') || '';
      const labelText =
        link.querySelector('label')?.textContent?.replace(/\s*\(\d+\)$/, '') ||
        '';
      const count = this.extractCount(
        link.querySelector('label')?.textContent || ''
      );
      const isActive = link.classList.contains('active');
      const normalized = this.normalizeTagHandle(labelText || href);
      newFilterData.set(href, { href, count, isActive, label: labelText });
      newFilterData.set(normalized, { href, count, isActive, label: labelText });
    });

    currentFilterLinks.forEach(link => {
      const href = link.getAttribute('href') || '';
      const labelElem = link.querySelector('label');
      const rawLabel = labelElem
        ? labelElem.textContent.replace(/\s*\(\d+\)$/, '')
        : '';
      const normalizedLabel = this.normalizeTagHandle(rawLabel || href);

      const newData =
        newFilterData.get(normalizedLabel) || newFilterData.get(href);

      if (newData) {
        if (labelElem) {
          if (newData.count > 0) {
            labelElem.textContent = `${newData.label} (${newData.count})`;
          } else {
            if (href.includes('car-parts')) {
              labelElem.textContent = newData.label;
            } else {
              labelElem.textContent = `${newData.label} (0)`;
            }
          }
        }

        if (newData.isActive) {
          link.classList.add('active');
          const checkbox = link.querySelector('input[type="checkbox"]');
          if (checkbox) checkbox.checked = true;
        } else {
          link.classList.remove('active');
          const checkbox = link.querySelector('input[type="checkbox"]');
          if (checkbox) checkbox.checked = false;
        }

        link.style.opacity = newData.count === 0 ? '0.5' : '';
      } else {
        link.style.opacity = ''; // do not grey if server missing info
      }
    });
  }

  /* -------------------------
     Pagination handling
  ------------------------- */
  handlePaginationClick(paginationLink) {
    const href = paginationLink.getAttribute('href');
    if (!href) return;

    // Convert relative URL to absolute URL
    const url = new URL(href, window.location.origin);
    
    // Preserve current chassis tags and filters
    const currentPathTags = this.getPathTagsArray();
    const currentQueryFilters = this.getQueryFilters();
    
    // Build the final URL with preserved filters
    const basePath = this.getCollectionBasePath(url.pathname);
    const pathTags = currentPathTags.length ? currentPathTags.join('+') : '';
    const path = pathTags ? `${basePath}/${pathTags}` : basePath;
    
    // Merge current query filters with pagination parameters
    const params = new URLSearchParams(url.search);
    
    // Add current non-pagination query filters
    if (currentQueryFilters._other) {
      for (const [key, value] of Object.entries(currentQueryFilters._other)) {
        if (key !== 'page') { // Don't preserve old page parameter
          params.set(key, value);
        }
      }
    }
    
    // Add current filter parameters
    for (const [key, values] of Object.entries(currentQueryFilters)) {
      if (key.startsWith('filter.')) {
        params.delete(key); // Remove existing values
        values.forEach(value => params.append(key, value));
      }
    }
    
    const finalUrl = this.buildUrl(path, [], params);
    
    // Update URL and load content via AJAX
    history.pushState({ filtersUrl: finalUrl }, '', finalUrl);
    this.loadFilters(finalUrl, false);
  }

  /* -------------------------
     Misc
  ------------------------- */
  extractCount(labelText) {
    const m = String(labelText).match(/\((\d+)\)$/);
    return m ? parseInt(m[1], 10) : 0;
  }

  handlePriceSubmit(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData);
    const baseUrl =
      form.getAttribute('action') || this.getCollectionBasePath(window.location.pathname);
    const current = new URLSearchParams(window.location.search);
    for (const [k, v] of current) {
      if (!params.has(k)) params.set(k, v);
    }
    params.delete('page');
    const url = `${baseUrl}?${params.toString()}`;
    history.pushState({ filtersUrl: url }, '', url);
    this.loadFilters(url, false);
  }

  handleSortChange(sortSelect) {
    const sortValue = sortSelect.value;
    const basePath = this.getCollectionBasePath(window.location.pathname);
    const newUrlObj = new URL(basePath, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);
    for (const [k, v] of currentParams) {
      if (k !== 'sort_by' && k !== 'page') newUrlObj.searchParams.set(k, v);
    }
    newUrlObj.searchParams.set('sort_by', sortValue);
    newUrlObj.searchParams.delete('page');

    const newUrl = newUrlObj.toString();
    history.pushState({ filtersUrl: newUrl }, '', newUrl);
    this.loadFilters(newUrl, false);
  }

  restoreSortSelection(url) {
    const newSortSelect = document.querySelector('[data-sort-by]');
    if (!newSortSelect) return;
    const urlObj = new URL(url, window.location.origin);
    const urlSortValue = urlObj.searchParams.get('sort_by');
    if (urlSortValue) {
      newSortSelect.value = urlSortValue;
      this.sortBySelect = newSortSelect;
    } else {
      const defaultSort = newSortSelect.getAttribute('data-default-sort');
      if (defaultSort) newSortSelect.value = defaultSort;
    }
  }

  initializeSortFromURL() {
    if (!this.sortBySelect) return;
    const currentUrl = new URL(window.location.href);
    const urlSortValue = currentUrl.searchParams.get('sort_by');
    if (urlSortValue) {
      this.sortBySelect.value = urlSortValue;
    } else {
      const defaultSortValue =
        this.sortBySelect.getAttribute('data-default-sort');
      if (defaultSortValue) this.sortBySelect.value = defaultSortValue;
    }
  }

  toggleMobileFilters() {
    if (this.filterSidebar) {
      this.filterSidebar.classList.toggle('filters-open');
      document.body.classList.toggle('filters-modal-open');
    }
  }

  showLoading() {
    if (this.loadingIndicator) this.loadingIndicator.style.display = 'block';
    if (this.productGrid) {
      this.productGrid.style.opacity = '0.5';
      this.productGrid.style.pointerEvents = 'none';
    }
  }

  hideLoading() {
    if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
    if (this.productGrid) {
      this.productGrid.style.opacity = '1';
      this.productGrid.style.pointerEvents = 'auto';
    }
  }
}

/* Init */
function initializeFilters() {
  new CollectionFilters();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFilters);
} else {
  initializeFilters();
}
